/**
 * Subscription explorer client wrapper around WebSocketClient
 * Provides high-level subscription methods for consensus and block explorer data
 */

import { WebSocketClient } from "../../transport/websocket/WebSocketClient";
import {
  SubscriptionClientConfig,
  SubscriptionCallback,
  ConnectionState,
} from "../../types";
import type { ConsensusEventFlat, TransactionEvent } from "../../types/api";

export class SubscriptionExplorer {
  private wsClient: WebSocketClient;
  private subscriptions: Map<string, SubscriptionCallback[]> = new Map();
  private wrappedCallbacks: Map<
    string,
    Map<SubscriptionCallback, SubscriptionCallback>
  > = new Map();

  /**
   * Constructor accepts either a WebSocketClient instance (to share with SubscriptionClient)
   * or a SubscriptionClientConfig (for backward compatibility when used independently)
   */
  constructor(wsClientOrConfig: WebSocketClient | SubscriptionClientConfig) {
    if (wsClientOrConfig instanceof WebSocketClient) {
      // Share the WebSocketClient instance from SubscriptionClient
      this.wsClient = wsClientOrConfig;
    } else {
      // Create a new WebSocketClient for backward compatibility
      const config = wsClientOrConfig;
      this.wsClient = new WebSocketClient({
        url: config.url,
        authToken: config.authToken,
        walletAddress: config.walletAddress,
        deviceID: config.deviceID,
      });
    }
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    await this.wsClient.connect();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.wsClient.disconnect();
    this.subscriptions.clear();
    this.wrappedCallbacks.clear();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.wsClient.isConnected();
  }

  /**
   * Get connection state
   */
  getState(): ConnectionState {
    return this.wsClient.getState();
  }

  /**
   * Register a callback for connection state changes
   * Returns an unsubscribe function
   */
  onStateChange(handler: (state: ConnectionState) => void): () => void {
    return this.wsClient.onStateChange(handler);
  }

  /**
   * Set authentication token, address, and device ID
   * Mirrors SetAuthToken from ws_manager.go
   * Delegates to WebSocketClient.setAuthToken
   */
  setAuthToken(token: string, address: string, deviceID: string): void {
    this.wsClient.setAuthToken(token, address, deviceID);
  }

  /**
   * Update authentication credentials
   * Now async to await reconnection
   * Delegates to WebSocketClient.updateAuth
   */
  async updateAuth(
    authToken?: string,
    walletAddress?: string,
    deviceID?: string,
  ): Promise<void> {
    return this.wsClient.updateAuth(authToken, walletAddress, deviceID);
  }

  /**
   * Check if ready for subscriptions (connected and authenticated if required)
   * Delegates to WebSocketClient.isReadyForSubscriptions
   */
  isReadyForSubscriptions(): boolean {
    return this.wsClient.isReadyForSubscriptions();
  }

  /**
   * Get current authentication state
   * Delegates to WebSocketClient.getAuthState
   */
  getAuthState(): "pending" | "authenticated" | "failed" | "not_required" {
    return this.wsClient.getAuthState();
  }

  /**
   * Wait for subscription acknowledgment
   * Delegates to WebSocketClient.waitForSubscription
   *
   * @param channel - Channel name to wait for
   * @param timeoutMs - Timeout in milliseconds (default: 5000)
   */
  async waitForSubscription(
    channel: string,
    timeoutMs: number = 5000,
  ): Promise<void> {
    return this.wsClient.waitForSubscription(channel, timeoutMs);
  }

  /**
   * Helper to parse router data field (can be JSON string or object)
   */
  private parseRouterData(data: any): any {
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error("Failed to parse router data field:", e);
        return null;
      }
    }
    return data;
  }

  /**
   * Helper to extract and parse data from router response
   */
  private extractRouterData(message: any): any | null {
    // Handle Router protocol: { type: "data", channel: "...", data: "..." } or { type: "data", channel: "...", data: {...} }
    if (message.type === "data" && message.data) {
      const parsed = this.parseRouterData(message.data);
      if (!parsed) {
        console.warn(
          "[SubscriptionExplorer] Failed to parse router data:",
          message.data,
        );
      }
      return parsed;
    }
    // Handle error responses
    if (message.type === "error" && message.error) {
      console.error("WebSocket error response:", message.error);
      return null;
    }
    // Handle ack responses (silently ignore)
    if (message.type === "ack") {
      return null;
    }
    // Log unexpected message format
    if (message.type !== "data") {
      console.debug(
        "[SubscriptionExplorer] Unexpected message type:",
        message.type,
        "Full message:",
        message,
      );
    } else if (!message.data) {
      console.warn(
        "[SubscriptionExplorer] Message type is 'data' but missing data field:",
        message,
      );
    }
    return null;
  }

  /**
   * Subscribe to consensus_flat updates
   * The consensus_flat channel streams flattened (linearized) consensus events from Morpheum's DAG-based consensus system.
   * This is designed for easier consumption by block explorers and clients, converting complex DAG structures into a simpler, block-like event stream.
   *
   * @param callback - Callback function for consensus event updates
   *                   Receives ConsensusEventFlat data with structure:
   *                   {
   *                     event_type: string (e.g., "new_vertex", "new_tx", "validator_update"),
   *                     data: ConsensusItemFlat (unified structure for vertices, transactions, validators),
   *                     source_shard: string,
   *                     event_time: number (Unix timestamp)
   *                   }
   * @param params - Optional subscription parameters (e.g., shard_id, event_type filter)
   */
  subscribeConsensusFlat(
    callback: (data: ConsensusEventFlat) => void,
    params?: {
      shard_id?: number | string;
      event_type?: string;
    },
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "consensus_flat";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      const consensusData = this.extractRouterData(message);
      if (consensusData) {
        callback(consensusData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Send subscription message using Router protocol format
    try {
      const subscriptionParams: any = {};
      if (params?.shard_id !== undefined) {
        subscriptionParams.shard_id = params.shard_id.toString();
      }
      if (params?.event_type) {
        subscriptionParams.event_type = params.event_type;
      }

      this.wsClient.send({
        type: "subscribe",
        channel: "consensus_flat",
        params:
          Object.keys(subscriptionParams).length > 0 ? subscriptionParams : {},
      });
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from consensus_flat updates
   * @param callback - Optional callback to remove specific subscription. If not provided, removes all subscriptions.
   */
  unsubscribeConsensusFlat(
    callback?: (data: ConsensusEventFlat) => void,
  ): void {
    const channel = "consensus_flat";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "consensus_flat",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to transactions channel updates
   * The transactions channel streams real-time transaction events from the consensus system.
   * This channel provides transaction lifecycle events (pending, confirmed, etc.) with
   * detailed transaction data including hash, vertex_hash, DAG depth, and status.
   *
   * Based on consensus_tx_test.go structure:
   * - Messages have structure: { type: "data", channel: "transactions", data: TransactionEvent }
   * - TransactionEvent contains: { event_type, data: { hash, txhash, ... }, source_shard, event_time }
   *
   * @param callback - Callback function for transaction event updates
   *                   Receives TransactionEvent data with structure:
   *                   {
   *                     event_type: string (e.g., "new_tx", "tx_confirmed", "tx_pending"),
   *                     data: TransactionEventData (contains hash, txhash, vertex_hash, status, etc.),
   *                     source_shard: string,
   *                     event_time: number (Unix timestamp)
   *                   }
   * @param params - Optional subscription parameters
   *                 - address: Filter transactions by wallet address
   *                 - txhash: Filter by specific transaction hash
   *                 - tx_types: Array of transaction types to filter
   *                 - shard_id: Filter by shard ID
   *                 - event_types: Array of event types to filter (e.g., ["new_tx", "tx_confirmed"])
   */
  subscribeTransactions(
    callback: (data: TransactionEvent) => void,
    params?: {
      address?: string;
      txhash?: string;
      tx_types?: string[];
      shard_id?: number | string;
      event_types?: string[];
    },
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "transactions";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // The data field contains a TransactionEvent with nested data structure
    const wrappedCallback = (message: any) => {
      const transactionData = this.extractRouterData(message);
      if (transactionData) {
        // Validate that we have a transaction event structure
        // The data should have event_type and data fields
        if (
          transactionData &&
          typeof transactionData === "object" &&
          transactionData.event_type
        ) {
          callback(transactionData as TransactionEvent);
        } else {
          console.warn(
            "[SubscriptionExplorer] Invalid transaction event structure:",
            transactionData,
          );
        }
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Send subscription message using Router protocol format
    try {
      const subscriptionParams: any = {};
      if (params?.address) {
        subscriptionParams.address = params.address;
      }
      if (params?.txhash) {
        subscriptionParams.txhash = params.txhash;
      }
      if (params?.tx_types && params.tx_types.length > 0) {
        subscriptionParams.tx_types = params.tx_types;
      }
      if (params?.shard_id !== undefined) {
        subscriptionParams.shard_id =
          typeof params.shard_id === "number"
            ? params.shard_id
            : parseInt(params.shard_id.toString(), 10);
      }
      if (params?.event_types && params.event_types.length > 0) {
        subscriptionParams.event_types = params.event_types;
      }

      this.wsClient.send({
        type: "subscribe",
        channel: "transactions",
        params:
          Object.keys(subscriptionParams).length > 0 ? subscriptionParams : {},
      });
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from transactions channel updates
   * @param callback - Optional callback to remove specific subscription. If not provided, removes all subscriptions.
   */
  unsubscribeTransactions(
    callback?: (data: TransactionEvent) => void,
  ): void {
    const channel = "transactions";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "transactions",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }
}
